import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ObjectObservable from "../utils/ObjectObservable";
import FileUploadResult from "../classes/FileUploadResult";
import ResultStatus from "../classes/ResultStatus";
import LabelWithInputField from "./LabelWithInputField";
import SuccessMessage from "../classes/SuccessMessage";
import FileToUpload from "../classes/FileToUpload";
import EventBus from "../utils/EventBus";
import MaterialIcons from "../icons/MaterialIcons";
import UserType from "../classes/UserType";
import Result from "../classes/Result";
import NOKUser from "../models/NOKUser";
import Button from "./Button";
import ListWrapper from "../utils/ListWrapper";
import PlatformClient from "../classes/PlatformClient";
import CheckServerErrors from "./CheckServerErrors";
import Browser from "../classes/Browser";
import TextView from "./TextView";
import Hospital from "../models/Hospital";
import RPCServices from "../rpc/RPCServices";
import CollectionUtils from "../utils/CollectionUtils";
import IconView from "./IconView";
import { Runnable } from "../classes/core";
import { BuildContext } from "../classes/BuildContext";

type _UpdateButtonOnPressed = (d3eState: UserProfileViewRefs) => void;

type _LabelwithInputField2OnChanged = (
  text: string,
  d3eState: UserProfileViewRefs
) => void;

type _LabelwithInputField8OnChanged = (
  text: string,
  d3eState: UserProfileViewRefs
) => void;

export interface UserProfileViewProps extends BaseUIProps {
  key?: string;
  user?: NOKUser;
}
/// To store state data for UserProfileView
class UserProfileViewRefs {
  public updateButton: UpdateButtonState = new UpdateButtonState();
}

interface UpdateButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: UserProfileViewRefs;
  _updateButtonHandler?: _UpdateButtonOnPressed;
}

class UpdateButtonState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
}

class _UpdateButtonWithState extends ObservableComponent<UpdateButtonWithStateProps> {
  updateButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: UpdateButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get updateButton(): UpdateButtonState {
    return this.props.d3eState.updateButton;
  }
  public get d3eState(): UserProfileViewRefs {
    return this.props.d3eState;
  }
  public get _updateButtonHandler(): _UpdateButtonOnPressed {
    return this.props._updateButtonHandler;
  }
  public initState() {
    super.initState();

    this.updateObservable("updateButton", null, this.updateButton);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(
      ["updateButton", "updateButton.", "updateButton.hover"],
      this.rebuild
    );
  }
  public updateButtonOnEnter(event): void {
    return this.updateButton.setHover(true);
  }
  public updateButtonOnExit(event): void {
    return this.updateButton.setHover(false);
  }
  public dispose(): void {
    this.updateButton.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      width: 120,
      child: Button({
        padding: this.updateButton.hover
          ? cStyle.tButtonPrimaryPaddingOnHover
          : cStyle.tButtonPrimaryPaddingOn,
        decoration: this.updateButton.hover
          ? cStyle.tButtonPrimaryDecorationOnHover
          : cStyle.tButtonPrimaryDecorationOn,
        disable: this.updateButton.disable,
        onPressed: () => {
          this._updateButtonHandler(this.d3eState);
        },
        onFocusChange: (val) => {},
        child: TextView({ data: "Update" }),
        onEnter: (event) => {
          this.updateButtonOnEnter(event);
        },
        onExit: (event) => {
          this.updateButtonOnExit(event);
        },
      }),
      className: "x33e hc",
    });
  }
}
function UpdateButtonWithState(props: UpdateButtonWithStateProps) {
  return React.createElement(_UpdateButtonWithState, props);
}

class _UserProfileViewState extends ObservableComponent<UserProfileViewProps> {
  static defaultProps = { user: null };
  d3eState: UserProfileViewRefs = new UserProfileViewRefs();
  errors: Array<string> = ListWrapper.widget(this, "errors");
  mobileErrorMessage: boolean = false;
  private _editInitListeners: Array<Runnable> = [];
  private _d3eModelErrors: Array<string> = [];
  private _nameErrors: Array<string> = [];
  private _emailErrors: Array<string> = [];
  public emailUniqueError: string;
  private _passwordErrors: Array<string> = [];
  private _statusDefaultValue: boolean = true;
  private _userTypeErrors: Array<string> = [];
  private _hospitalErrors: Array<string> = [];
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: UserProfileViewProps) {
    super(props);

    this.initState();
  }
  public get user(): NOKUser {
    return this.props.user;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.updateSyncProperty("user", this.props.user);

    this.on(
      [
        "errors",
        "user",
        "user.email",
        "user.hospital",
        "user.hospital.address",
        "user.hospital.name",
        "user.mobile",
        "user.name",
        "user.profile",
      ],
      this.rebuild
    );
  }
  public componentDidUpdate(prevProps: UserProfileViewProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.user !== this.props.user) {
      this.updateObservable("user", prevProps.user, this.props.user);

      this.fire("user", this);
    }
  }
  public setErrors(val: Array<string>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(this.errors, val);

    if (!isValChanged) {
      return;
    }

    this.errors.clear();

    this.errors.addAll(val);

    this.fire("errors", this);
  }
  public addToErrors(val: string, index: number = -1): void {
    if (index === -1) {
      if (!this.errors.contains(val)) this.errors.add(val);
    } else {
      this.errors.remove(this.errors.elementAt(index));

      this.errors.add(val);
    }

    this.fire("errors", this, val, true);
  }
  public removeFromErrors(val: string): void {
    this.errors.remove(val);

    this.fire("errors", this, val, false);
  }
  public setMobileErrorMessage(val: boolean): void {
    let isValChanged: boolean = this.mobileErrorMessage !== val;

    if (!isValChanged) {
      return;
    }

    this.mobileErrorMessage = val;

    this.fire("mobileErrorMessage", this);
  }
  public get d3eModelErrors(): Array<string> {
    return this._d3eModelErrors ?? [];
  }
  public set d3eModelErrors(val: Array<string>) {
    this._d3eModelErrors.clear();

    this._d3eModelErrors.addAll(val);
  }
  public get nameErrors(): Array<string> {
    return this._nameErrors ?? [];
  }
  public set nameErrors(val: Array<string>) {
    this._nameErrors.clear();

    this._nameErrors.addAll(val);
  }
  public async computeNameErrors(): Promise<void> {
    this.nameErrors = [];

    let it: string = this.user.name;

    if (it == null || it === "") {
      this.nameErrors.add("Name cannot be empty.");
    }

    this.fire("nameErrors", this);
  }
  public get emailErrors(): Array<string> {
    return this._emailErrors ?? [];
  }
  public set emailErrors(val: Array<string>) {
    this._emailErrors.clear();

    this._emailErrors.addAll(val);
  }
  public async computeEmailErrors(): Promise<void> {
    this.emailErrors = [];

    let it: string = this.user.email;

    if (it == null || it === "") {
      this.emailErrors.add("Email cannot be empty.");
    }

    this.fire("emailErrors", this);
  }
  public async checkNOKUserEmailUniqueInHospital(): Promise<void> {
    if (this.user == null || this.user.hospital == null) {
      return;
    }

    let isUnique: boolean =
      await RPCServices.getUniqueChecker().checkNOKUserEmailUniqueInHospital(
        this.user.hospital,
        this.user,
        this.user.email
      );

    if (!isUnique) {
      this.emailUniqueError = "Duplicate email found with this value.";
    }
  }
  public get passwordErrors(): Array<string> {
    return this._passwordErrors ?? [];
  }
  public set passwordErrors(val: Array<string>) {
    this._passwordErrors.clear();

    this._passwordErrors.addAll(val);
  }
  public async computePasswordErrors(): Promise<void> {
    this.passwordErrors = [];

    let it: string = this.user.password;

    if (it == null || it === "") {
      this.passwordErrors.add("Password cannot be empty.");
    }

    this.fire("passwordErrors", this);
  }
  public get userTypeErrors(): Array<string> {
    return this._userTypeErrors ?? [];
  }
  public set userTypeErrors(val: Array<string>) {
    this._userTypeErrors.clear();

    this._userTypeErrors.addAll(val);
  }
  public async computeUserTypeErrors(): Promise<void> {
    this.userTypeErrors = [];

    let it: UserType = this.user.userType;

    this.fire("userTypeErrors", this);
  }
  public get hospitalErrors(): Array<string> {
    return this._hospitalErrors ?? [];
  }
  public set hospitalErrors(val: Array<string>) {
    this._hospitalErrors.clear();

    this._hospitalErrors.addAll(val);
  }
  public async computeHospitalErrors(): Promise<void> {
    this.hospitalErrors = [];

    let it: Hospital = this.user.hospital;

    if (it == null) {
      this.hospitalErrors.add("Hospital cannot be null.");
    }

    this.fire("hospitalErrors", this);
  }
  public setEditDefaults(): void {}
  public get isEditValid(): boolean {
    if (this.nameErrors.isNotEmpty) {
      return false;
    }

    if (this.emailErrors.isNotEmpty) {
      return false;
    }

    if (this.passwordErrors.isNotEmpty) {
      return false;
    }

    if (this.userTypeErrors.isNotEmpty) {
      return false;
    }

    if (this.hospitalErrors.isNotEmpty) {
      return false;
    }

    return true;
  }
  public validate(): Array<string> {
    let errors: Array<string> = [];

    this.computeNameErrors();

    errors.addAll(this.nameErrors);

    this.computeEmailErrors();

    errors.addAll(this.emailErrors);

    this.computePasswordErrors();

    errors.addAll(this.passwordErrors);

    this.computeUserTypeErrors();

    errors.addAll(this.userTypeErrors);

    this.computeHospitalErrors();

    errors.addAll(this.hospitalErrors);

    return errors;
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Column({
      crossAxisAlignment: ui.CrossAxisAlignment.start,
      mainAxisAlignment: ui.MainAxisAlignment.start,
      children: [
        ui.Column({
          crossAxisAlignment: ui.CrossAxisAlignment.start,
          mainAxisAlignment: ui.MainAxisAlignment.start,
          children: [
            ui.Column({
              crossAxisAlignment: ui.CrossAxisAlignment.start,
              mainAxisAlignment: ui.MainAxisAlignment.start,
              children: [
                ui.Container({
                  child:
                    this.user.profile !== null
                      ? ui.ClipRRect({
                          borderRadius: ui.BorderRadius.circular(50),
                          child: ui.NetworkImage({
                            url: this.user.profile.downloadUrl,
                            width: 100,
                            height: 100,
                            fit: ui.BoxFit.fill,
                            className: "xeef hc vc",
                          }),
                        })
                      : ui.Container({
                          alignment: ui.Alignment.center,
                          child: IconView({
                            icon: MaterialIcons.person,
                            size: 40,
                            className: "xfb2a hc vc",
                          }),
                          className: "xd04 hc vc",
                        }),
                  onTap: (e) => {
                    e.stopPropagation();

                    this.onProfileImageTap(this.d3eState);
                  },
                  className: "hc",
                  key: "0",
                }),
                ui.Row({
                  mainAxisAlignment: ui.MainAxisAlignment.start,
                  children: [
                    ui.Container({
                      width: 400,
                      child: LabelWithInputField({
                        name: "Name",
                        placeHolder: "Enter Name",
                        isRequired: true,
                        value: this.user.name,
                        errors:
                          this.user.name === "" ? ["Name is required"] : [],
                        onChanged: (text) => {
                          this.labelwithInputField2onChanged(
                            text,
                            this.d3eState
                          );
                        },
                      }),
                      key: "0",
                      className: "x7af hc",
                    }),
                    ui.Container({
                      width: 400,
                      child: LabelWithInputField({
                        placeHolder: "Enter Email ID",
                        name: "Email ID",
                        value: this.user.email,
                        disable: true,
                      }),
                      key: "1",
                      className: "x0d6 hc",
                    }),
                  ],
                  className: "x151 hc h",
                  key: "1",
                }),
                ui.Container({
                  padding: ui.EdgeInsets.fromLTRB(
                    0.0,
                    0.0,
                    0.0,
                    5.0,
                    new Map()
                  ),
                  width: 400,
                  child: LabelWithInputField({
                    placeHolder: "Enter Mobile Number",
                    name: "Mobile Number",
                    value: this.user.mobile,
                    onChanged: (text) => {
                      this.labelwithInputField8onChanged(text, this.d3eState);
                    },
                  }),
                  key: "2",
                  className: "x2e9 hc",
                }),
                TextView({
                  data: "Hospital Details",
                  style: new ui.TextStyle({
                    fontSize: cStyle.tTextViewHeadlineFiveFontSizeOn,
                    fontWeight: cStyle.tTextViewHeadlineFiveFontWeightOn,
                  }),
                  className: "xbc hc",
                  key: "3",
                }),
                ui.Row({
                  mainAxisAlignment: ui.MainAxisAlignment.start,
                  children: [
                    LabelWithInputField({
                      name: "Hospital Name",
                      value: this.user.hospital.name,
                      disable: true,
                      key: "0",
                    }),
                  ],
                  className: "x7e1 hc h",
                  key: "4",
                }),
                LabelWithInputField({
                  name: "Address",
                  placeHolder: "Enter Address",
                  value: this.user.hospital?.address ?? "",
                  fieldHeight: 70,
                  maxLines: 4,
                  className: "xefc hc h",
                  key: "5",
                }),
              ],
              className: "x79cb hc h",
              key: "0",
            }),
            ui.Column({
              mainAxisAlignment: ui.MainAxisAlignment.start,
              crossAxisAlignment: ui.CrossAxisAlignment.start,
              children: [
                this.errors.isNotEmpty
                  ? CheckServerErrors({ errors: this.errors, className: "hc" })
                  : [],
                ui.Row({
                  crossAxisAlignment: ui.CrossAxisAlignment.start,
                  children: [
                    UpdateButtonWithState({
                      d3eState: this.d3eState,
                      _updateButtonHandler: this.updateButtonHandler,
                      key: "0",
                    }),
                  ],
                  className: "x5af hc h",
                  key: "1",
                }),
              ],
              className: "x050 hc h",
              key: "1",
            }),
          ],
          className: "xcb7 hc h",
          key: "0",
        }),
      ],
      className: ui.join(
        this.props.className,
        "UserProfileView x915 hc vc h v"
      ),
      ...copyBaseUIProps(this.props),
    });
  }
  public onDispose = (): void => {
    this.user.restore();
  };
  public updateButtonHandler = async (
    d3eState: UserProfileViewRefs
  ): Promise<void> => {
    if (this.validate().isEmpty) {
      let res: Result<NOKUser> = await this.user.save();

      if (res.status === ResultStatus.Success) {
        EventBus.get().fire(
          new SuccessMessage({ message: "Successfully saved" })
        );
      } else {
        this.setErrors(res.errors);
      }
    }
  };
  public onProfileImageTap = async (
    d3eState: UserProfileViewRefs
  ): Promise<void> => {
    let fileToUpload: FileToUpload = await Browser.selectFile([], () => {});

    if (fileToUpload !== null) {
      let res: FileUploadResult = await PlatformClient.upload(fileToUpload);

      if (res.success) {
        this.user.setProfile(res.file);
      }
    }
  };
  public dispose(): void {
    super.dispose();

    this.onDispose();
  }
  public labelwithInputField2onChanged = (
    val: string,
    d3eState: UserProfileViewRefs
  ): void => {
    this.user.setName(val);
  };
  public labelwithInputField8onChanged = (
    val: string,
    d3eState: UserProfileViewRefs
  ): void => {
    this.user.setMobile(val);
  };
  public get updateButton() {
    return this.d3eState.updateButton;
  }
}
export default function UserProfileView(props: UserProfileViewProps) {
  return React.createElement(_UserProfileViewState, {
    ..._UserProfileViewState.defaultProps,
    ...props,
  });
}
